import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

// CPK-4585: shared Cloudflare Turnstile widget — first real usage in this app
// (the script tag lives in index.html). Renders once the global `turnstile`
// object is available (it loads async/defer, so it may not be ready yet on
// first ngAfterViewInit) and emits the token via (tokenReceived).
@Component({
  selector: 'app-turnstile-widget',
  template: '<div #container></div>',
})
export class TurnstileWidgetComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;
  @Output() tokenReceived = new EventEmitter<string>();
  @Output() tokenExpired = new EventEmitter<void>();

  private widgetId: string | null = null;
  private pollHandle: ReturnType<typeof setInterval> | null = null;

  ngAfterViewInit(): void {
    if (window.turnstile) {
      this.render();
      return;
    }
    // The script loads async — poll briefly instead of assuming it's ready.
    this.pollHandle = setInterval(() => {
      if (window.turnstile) {
        clearInterval(this.pollHandle!);
        this.pollHandle = null;
        this.render();
      }
    }, 200);
  }

  private render(): void {
    this.widgetId = window.turnstile!.render(this.container.nativeElement, {
      sitekey: environment.turnstileSiteKey,
      callback: (token: string) => this.tokenReceived.emit(token),
      'expired-callback': () => this.tokenExpired.emit(),
      'error-callback': () => this.tokenExpired.emit(),
    });
  }

  reset(): void {
    if (this.widgetId) window.turnstile?.reset(this.widgetId);
  }

  ngOnDestroy(): void {
    if (this.pollHandle) clearInterval(this.pollHandle);
    if (this.widgetId) window.turnstile?.remove(this.widgetId);
  }
}
